/* eslint-disable @typescript-eslint/no-magic-numbers */
import {
  CURR_ENV,
  STATIC_API_BASE_URL,
} from '@/core/constants/appConfig/environment';
import { EScriptEnv } from '@/core/constants/appConfig/types';
import { StorageHandler } from '@/core/data_handlers/storage_handler';
import { ScriptHandler } from '@/core/handlers/script';
import EntryPoint from '@/core/main';
import {
  getDatalayerBaseName,
  getDatalayerWrapperFuncName,
} from '@/core/wtag/datalayer/datalayer_variables';
import { Logger } from '@/shared/logger/logger';
import { initWorkers } from '@/workers/init_workers';

import { EBundleName } from '../index';

// Helper function to add preload hints for worker scripts
function addWorkerPreloadHints() {
  if (typeof document === 'undefined') return;

  const workerUrls = ['ews-worker.js', 'events-worker.js'];

  // Get base URL for workers
  const baseUrl =
    CURR_ENV === EScriptEnv.TEST || CURR_ENV === EScriptEnv.LOCAL
      ? `${STATIC_API_BASE_URL}/dist/`
      : `${STATIC_API_BASE_URL}/static2/`;

  workerUrls.forEach(workerUrl => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'script';
    // Use URL constructor for robust URLs
    link.href = new URL(workerUrl, baseUrl).href;
    // Add crossOrigin attribute for proper CORS handling
    link.crossOrigin = 'anonymous';
    // Check if a preload link with the same href already exists
    if (!document.querySelector(`link[rel="preload"][href="${link.href}"]`)) {
      document.head.append(link);
    }
  });
}

export function initSourceEntryPoint(bundleName: EBundleName): string {
  // eslint-disable-next-line unicorn/consistent-function-scoping
  const getDebugSettings = () => {
    const urlParams = new URLSearchParams(new URL(document.URL).search);
    const scriptParams = ScriptHandler.getInstance().getURLParams();
    const storage = new StorageHandler('wgs_debug_mode', 'false');
    const logStorage = new StorageHandler('wgs_log_level', 'NONE');

    const debug_mode =
      scriptParams?.get('wgs_debug_mode') === 'true' ||
      urlParams.get('wgs_debug_mode') === 'true' ||
      storage.get() === 'true' ||
      false;
    const loglevel =
      scriptParams?.get('wgs_log_level') ||
      urlParams.get('wgs_log_level') ||
      logStorage.get() ||
      'NONE';

    storage.set(debug_mode.toString());
    logStorage.set(loglevel);

    return { debug_mode, loglevel };
  };

  const initLogger = () => {
    Logger.setBundleName(bundleName);
    Logger.setEnvironment(CURR_ENV);
    const { debug_mode, loglevel } = getDebugSettings();
    Logger.setLevelAsString(loglevel);
    // Disable unique profile keys to avoid duplicate logs for the same function
    Logger.setUseUniqueProfileKeys(false);
    Logger.info(`Debug mode: ${debug_mode} LogLevel: ${loglevel}`);
  };

  // eslint-disable-next-line unicorn/consistent-function-scoping
  const initDataLayer = () => {
    const dataLayerKey = getDatalayerBaseName();
    const wtagFunctionKey = getDatalayerWrapperFuncName();
    (window as any)[dataLayerKey] = (window as any)[dataLayerKey] || [];
    (window as any)[wtagFunctionKey] = (...args: any[]) =>
      (window as any)[dataLayerKey].push(args);
    return wtagFunctionKey;
  };

  // Initialize background workers
  const initBackgroundWorkers = () => {
    // Only initialize workers if we're in a browser environment
    if (typeof window !== 'undefined') {
      // Check for Worker support
      if (typeof Worker === 'undefined') {
        Logger.warn('Web Workers not supported in this environment');
      } else {
        // Initialize workers immediately without delay
        initWorkers();
        Logger.info('Background workers initialization started');
      }
    }
  };
  initBackgroundWorkers();
  initLogger();
  const wtagFunctionKey = initDataLayer();

  Logger.info(`${bundleName} entrypoint with mode: ${CURR_ENV}`);

  Logger.info(`${bundleName} entrypoint with mode: ${CURR_ENV}`);

  EntryPoint();
  return wtagFunctionKey;
}
